import { forwardRef, memo, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import translationKeys from '../../../translations/keys';

import useWhiteLabelComponent from '../../../hooks/useWhiteLabelComponent';
import useKeyboardZoomControls from '../../../hooks/useKeyboardZoomControls';
import useResizeOberserver from '../../../hooks/useResizeObserver';

import { getMediaSnapshot } from '../../../utils/video';

import { ReactComponent as CloseIcon } from '../../../assets/images/icons/close.svg';

import { VideoPlayerWithControls } from '../../video-player';
import Loading from '../../loading';
import P from '../../p';
import { ModalWrapper } from '../Modal';
import { ZoomableContent, ZoomableWrapper, ZoomControls } from '../../zoomable-wrapper';
import {
    ModalPlayerContainer,
    ModalPlayerHeader,
    ModalPlayerTitle,
    ModalPlayerCloseButton,
    ModalPlayerFooter,
    ContentWrapper,
    ImageElement,
    ModalPlayerFooterContent,
    ModalPlayerFooterLeft,
    ModalPlayerFooterActions,
    VideoContainer,
    MediaPlaceholder,
    MediaErrorMessageContainer,
    ZoomableImageContainer,
} from './ModalPlayer.styles';

/** Modal for playing a video or displaying an image. Content for footer is passed through children. */
const ModalPlayer = withTranslation()(
    ({
        show = false,
        closeModal,
        loading = false,
        error,
        mediaLoading = false,
        mediaError,
        headerText,
        videoConfig,
        imageConfig,
        children,
        closeRef,
        containerRef,
        snapshotInfo,
        t,
        ...props
    }) => {

        const { navColour: headerColour, navTextColour: headerTextColour, accentColour: spinnerColour } = useWhiteLabelComponent();

        useEffect(() => {
            // If ModalPlayer is loading when it first opens, useModal hook won't be able to put focus on close button (as it is not mounted)
            // So do that here when it stops loading
            if (show && !loading) {
                closeRef?.current?.focus();
            }
        }, [closeRef, show, loading]);

        return (
            <ModalWrapper
                show={show}
                closeModal={closeModal}
                containerRef={containerRef}
            >
                {error?.length > 0 ? (
                    <P style={{ minWidth: '70vw' }} centre colour="#fff">
                        {error}
                    </P>
                ) : loading ? (
                    <Loading spinnerColour={spinnerColour} />
                ) : (
                    <ModalPlayerContainer
                        {...props}
                    >
                        {/* Header */}
                        <ModalPlayerHeader
                            $backgroundColour={headerColour}
                            $textColour={headerTextColour}
                        >
                            <ModalPlayerTitle>{headerText}</ModalPlayerTitle>
                            <ModalPlayerCloseButton
                                ref={closeRef}
                                aria-label={t(translationKeys.actions.CLOSE)}
                                onClick={closeModal}
                                $colour={headerTextColour}
                            >
                                <CloseIcon />
                            </ModalPlayerCloseButton>
                        </ModalPlayerHeader>

                        {/* Body */}
                        <ContentWrapper>
                            {
                                (mediaLoading || mediaError) ? (
                                    <MediaPlaceholder>
                                        {
                                            mediaLoading ? (
                                                <Loading fill />
                                            ) : (
                                                <MediaErrorMessageContainer>
                                                    {
                                                        typeof mediaError === 'string' ? (
                                                            <p>{mediaError}</p>
                                                        ) : (
                                                            <>
                                                                { mediaError.heading && <h2>{mediaError.heading}</h2> }
                                                                {
                                                                    (Array.isArray(mediaError.text) ? mediaError.text : [mediaError.text]).map((text, index) => (
                                                                        <p key={index}>{text}</p>
                                                                    ))
                                                                }
                                                            </>
                                                        )
                                                    }
                                                </MediaErrorMessageContainer>
                                            )
                                        }
                                    </MediaPlaceholder>
                                ) : videoConfig ? (
                                    <VideoContainer>
                                        <VideoPlayerWithControls
                                            width="100%"
                                            height="100%"
                                            {...videoConfig}
                                            // TODO: Handle errors with onError prop
                                        />
                                    </VideoContainer>
                                ) : imageConfig ? (
                                    <ZoomableImage
                                        {...imageConfig}
                                    />
                                ) : null}
                        </ContentWrapper>

                        {/* Footer */}
                        <ModalPlayerFooter>{children}</ModalPlayerFooter>
                    </ModalPlayerContainer>
                )}
            </ModalWrapper>
        );
    }
);

// Component for showing images and zooming/rotating them
// As soon as this is needed elsewhere, make it its own standalone component like VideoPlayer
// Images are rendered according to their initial aspect ratio, then rotating occurs within that fixed area
const ZoomableImage = memo(forwardRef(({ zoomControls = false, rotateControls = false, ...props }, ref) => {

    const zoomControlsRef = useRef();
    const containerRef = useRef();
    useKeyboardZoomControls(zoomControlsRef, containerRef, !zoomControls);

    // Identify image aspect ratio
    const [[imageWidth, imageHeight], setImageWidthHeight] = useState([]);
    useLayoutEffect(() => {
        const image = new Image();
        image.onload = () => {
            setImageWidthHeight([image.width, image.height]);
        }
        image.src = props.src;
    }, [props.src]);
    
    // Identify container size
    const [[containerWidth, containerHeight], setContainerWidthHeight] = useState([]);
    useResizeOberserver(containerRef, ({ width, height }) => {
        setContainerWidthHeight([width, height]);
    });

    // 0, 90, 180, 270
    const [rotation, setRotation] = useState(0);

    const imageRef = useRef();

    // Control snapshot animation
    const [showSnapshotOverlay, setShowSnapshotOverlay] = useState(false);
    useEffect(() => {
        if (showSnapshotOverlay) {
            // Clear overlay very soon after it's shown to create snapshot animation
            const timeout = setTimeout(() => setShowSnapshotOverlay(false), 200);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [showSnapshotOverlay]);

    useImperativeHandle(ref, () => {
        return {
            container: containerRef.current,
            takeSnapshot: () => {
                setShowSnapshotOverlay(true);
                const canvas = getMediaSnapshot(
                    imageRef.current,
                    containerRef.current,
                    zoomControlsRef.current.instance.transformState
                );
                return canvas;
            }
        };
    }, []);

    return (
        <ZoomableImageContainer
            ref={containerRef}
            tabIndex={zoomControls ? 0 : undefined}
            $imageWidth={imageWidth}
            $imageHeight={imageHeight}
            $snapshotOverlay={showSnapshotOverlay}
        >
            <ZoomableWrapper
                ref={zoomControlsRef}
                disabled={!zoomControls}
            >
                <ZoomableContent>
                    
                    <ImageElement
                        ref={imageRef}
                        {...props}
                        style={{
                            // On certain screen sizes, we were seeing image rendering smaller than specified
                            // so added min width/height.
                            width: rotation % 180 === 0 ? '100%' : containerHeight,
                            minWidth: rotation % 180 === 0 ? '100%' : containerHeight,
                            height: rotation % 180 === 0 ? '100%' : containerWidth,
                            minHeight: rotation % 180 === 0 ? '100%' : containerWidth,
                            transform: `rotateZ(${rotation}deg)${rotation % 180 === 0 ? '' : ` translate(${rotation >= 180 ? -containerHeight : 0}px, ${rotation % 270 === 0 ? 0 : -containerWidth}px)`}`,
                            transformOrigin: rotation % 180 === 0 ? 'center' : 'top left'
                        }}
                    />
                </ZoomableContent>

                {
                    zoomControls && (
                        <ZoomControls
                            onReset={() => setRotation(0)}
                            onRotate={rotateControls ? () => {
                                setRotation(currentRotation => {
                                    return currentRotation === 0 ? 270 : currentRotation - 90;
                                });
                            } : undefined}
                        />
                    )
                }
            </ZoomableWrapper>
        </ZoomableImageContainer>
    );
}));

ModalPlayer.FooterLeft = ModalPlayerFooterLeft;
ModalPlayer.FooterActions = ModalPlayerFooterActions;
ModalPlayer.FooterContent = ModalPlayerFooterContent;

ModalPlayer.propTypes = {
    /** Show the player. */
    show: PropTypes.bool,
    /** Function to close the player. Called when close icon or background is clicked. */
    closeModal: PropTypes.func.isRequired,
    /** Ref to attach to close button. */
    closeRef: PropTypes.shape({
        current: PropTypes.oneOfType([PropTypes.element, PropTypes.object])
    }),
    /** Ref to attach to container. Use to trap focus within this element. */
    containerRef: PropTypes.shape({
        current: PropTypes.oneOfType([PropTypes.element, PropTypes.object])
    }),
    /** Loading spinner shown instead of modal while this is true. Use this when the modal cannot be shown yet. If you're only waiting for the media to load, use `mediaLoading` instead. */
    loading: PropTypes.bool,
    /** Loading spinner shown instead of video/image. */
    mediaLoading: PropTypes.bool,
    /** Error message shown instead of modal. Use this when the error means you cannot show the modal at all. If only the media has failed, use `mediaError` instead. */
    error: PropTypes.string,
    /** Error message shown instead of video/image. */
    mediaError: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
        heading: PropTypes.string,
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
    })]),
    /** Loading component shown instead of modal when `loading` prop is true. */
    Loading: PropTypes.elementType,
    /** Text shown in header. */
    headerText: PropTypes.string,
    /** When using the modal to play a video, this must be an object containing the required props for ReactPlayer. */
    videoConfig: PropTypes.object,
    /** When using the modal to display an image, this must be an object containing required props to pass to <img /> (e.g. src, alt). Can also contain boolean `zoomControls` and `rotateControls` to let the user zoom/pan/rotate the image (`zoomControls` must be true for `rotateControls` to take effect). */
    imageConfig: PropTypes.object,
    /** Content for left side of footer top row. */
    footerLeft: PropTypes.node,
    /** Array of icons for right side of footer top row. */
    actionIcons: PropTypes.arrayOf(PropTypes.node),
    /** Content for bottom of footer. */
    footerContent: PropTypes.node,
};

export default ModalPlayer;
